import { useEffect, useState } from 'react';
import { didApi } from '../api/tenants.js';

/**
 * useTenantDids — 該租戶的「進線路由」= avatarId → 綁了哪些門號
 *
 * 為什麼要有這支（0921）：
 *   角色卡的「未綁門號」標籤原本讀 `avatars/{id}.did`，但那是**沒人寫的孤兒欄位** ——
 *   綁定其實只寫 `tenants/{host}/sipPhoneNumbers`（admin.js PUT /dids/:phoneNumber），
 *   從來沒有同步回角色文件。實測：畫面看得到的 91 隻已綁角色有 82 隻被標成「未綁門號」，
 *   而有 did 值的 15 隻裡 7 隻會顯示一支現在不屬於它的號碼（1 隻綁著但指錯、6 隻早已解綁）。
 *   真正的通話路由吃的是 sipPhoneNumbers（sipRelay.js `sipPhoneDb.get`），
 *   所以電話一直接得到 —— 錯的只有畫面。這支把畫面改讀同一份權威資料。
 *
 * ⚠️ 一隻角色可以綁多支門號（實測 23 隻，最多 5 支），所以回陣列不回單值 ——
 *   單一 did 欄位在結構上就裝不下，那正是舊做法的第二個問題。
 *
 * 顯示用 representativeNumber（人看得懂的代表號 0277027154），
 * 沒填才退回 phoneNumber（trunk code 如 748）。
 * `enabled === false` 的對應不算綁定 —— sipRelay 會拒接（`did mapping disabled`）。
 */
// ─── did map (test-slice-start) ───
/** sipPhoneNumbers 清單 → { avatarId: ['0277027154', …] }（代表號優先、順序穩定） */
function buildDidMap(dids) {
    const staged = {};
    for (const d of dids || []) {
        if (!d || !d.avatarId || d.enabled === false) continue;
        const rep = d.representativeNumber;
        const label = rep || d.phoneNumber;
        if (!label) continue;
        const key = String(label);
        const list = (staged[d.avatarId] = staged[d.avatarId] || []);
        // 同一支號碼可能同時以 doc id 與 representativeNumber 出現 → 去重，免得 chip 顯示「+1」但其實只有一支
        if (list.some((e) => e.key === key)) continue;
        list.push({ key, hasRep: !!rep });
    }

    // chip 只放得下第一支（其餘變「+N」放 tooltip），所以第一支必須是人看得懂的那支：
    //   有代表號（0277027614）的排前面，沒填而退回 trunk code（328）的排後面。
    //   ⚠️ 不排序的話第一支取決於 Firestore 回傳順序 —— 台鐵客服 5 支就會顯示成「已綁 328 +4」，
    //     看的人不知道那是什麼。同類再按號碼排，讓同一張卡每次重整顯示一致。
    const out = {};
    for (const [avatarId, list] of Object.entries(staged)) {
        list.sort((a, b) => (Number(b.hasRep) - Number(a.hasRep)) || a.key.localeCompare(b.key));
        out[avatarId] = list.map((e) => e.key);
    }
    return out;
}
// ─── did map (test-slice-end) ───

/**
 * @returns {Object<string, string[]> | null}
 *   null = 還不知道（載入中，或 API 失敗）；{} = 查過了、這個租戶沒有任何綁定。
 *   ⚠️ 兩者必須分開：這張票的病根就是「標籤在說謊」。如果載入中／失敗都退回 {}，
 *     每次進頁面 82 張卡會先閃一下「未綁門號」再翻成「已綁」，而任何一次 401/500
 *     會靜默重現原本的假陰性。呼叫端拿到 null 就什麼都別畫，不要畫「未綁門號」。
 */
export function useTenantDids(hostId) {
    const [didsByAvatar, setDidsByAvatar] = useState(null);

    useEffect(() => {
        if (!hostId) { setDidsByAvatar(null); return; }
        let cancelled = false;
        // 切租戶先回「不知道」—— 否則上一個租戶的綁定會短暫套在新租戶的卡上
        setDidsByAvatar(null);
        didApi.listByHost(hostId)
            .then((r) => {
                if (!cancelled) setDidsByAvatar(buildDidMap(r.dids));
            })
            // 失敗就維持「不知道」；標籤空著比說錯話好，也不要把整頁擋掉
            .catch(() => { if (!cancelled) setDidsByAvatar(null); });
        return () => { cancelled = true; };
    }, [hostId]);

    return didsByAvatar;
}
